import { useRef, useEffect } from 'react';

/**
 * Creates DOM element to be used as React root.
 */
function createRootElement(id: string): HTMLElement {
    const rootContainer = document.createElement('div');
    rootContainer.setAttribute('id', id);

    return rootContainer;
}

/**
 * Appends element as last child of body.
 */
function addRootElement(rootElem: HTMLElement) {
    if (document.body.lastElementChild) {
        document.body.insertBefore(rootElem, document.body.lastElementChild.nextElementSibling);
    }
}

/**
 * Hook to create a React Portal.
 * Automatically handles creating and tearing-down the root elements (no SRR
 * makes this trivial), so there is no need to ensure the parent target already
 * exists.
 * @example
 * const target = usePortal(id, [id]);
 * return createPortal(children, target);
 */
function usePortal(id: string): HTMLDivElement {
    const rootElemRef = useRef(null);

    useEffect(() => {
        const existingParent = document.querySelector(`#${id}`) as HTMLElement;
        const parentElem = existingParent || createRootElement(id);

        if (!existingParent) {
            addRootElement(parentElem);
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        parentElem.appendChild(rootElemRef.current);

        return () => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            rootElemRef.current.remove();
        };
    }, [id]);

    /**
     * It's important we evaluate this lazily:
     * - We need first render to contain the DOM element, so it shouldn't happen
     *   in useEffect. We would normally put this in the constructor().
     * - We can't do 'const rootElemRef = useRef(document.createElement('div))',
     *   since this will run every single render (that's a lot).
     * - We want the ref to consistently point to the same DOM element and only
     *   ever run once.
     * @link https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
     */
    function getRootElem(): HTMLDivElement {
        if (!rootElemRef.current) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return (rootElemRef.current = document.createElement('div'));
        }

        return rootElemRef.current as unknown as HTMLDivElement;
    }

    return getRootElem();
}

export default usePortal;
